import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { Button } from './button.js';

const CarouselContext = React.createContext(null);
function useCarousel() {
    const context = React.useContext(CarouselContext);
    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }
    return context;
}
const Carousel = React.forwardRef((_a, ref) => {
    var { orientation = "horizontal", opts, setApi, plugins, className, children } = _a, props = __rest(_a, ["orientation", "opts", "setApi", "plugins", "className", "children"]);
    const [carouselRef, api] = useEmblaCarousel(Object.assign(Object.assign({}, opts), { axis: orientation === "horizontal" ? "x" : "y" }), plugins);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const onSelect = React.useCallback((api) => {
        if (!api) {
            return;
        }
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);
    const scrollPrev = React.useCallback(() => {
        api === null || api === void 0 ? void 0 : api.scrollPrev();
    }, [api]);
    const scrollNext = React.useCallback(() => {
        api === null || api === void 0 ? void 0 : api.scrollNext();
    }, [api]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            scrollPrev();
        }
        else if (event.key === "ArrowRight") {
            event.preventDefault();
            scrollNext();
        }
    }, [scrollPrev, scrollNext]);
    React.useEffect(() => {
        if (!api || !setApi) {
            return;
        }
        setApi(api);
    }, [api, setApi]);
    React.useEffect(() => {
        if (!api) {
            return;
        }
        onSelect(api);
        api.on("reInit", onSelect);
        api.on("select", onSelect);
        return () => {
            api === null || api === void 0 ? void 0 : api.off("select", onSelect);
        };
    }, [api, onSelect]);
    return (jsx(CarouselContext.Provider, Object.assign({ value: {
            carouselRef,
            api: api,
            opts,
            orientation: orientation || ((opts === null || opts === void 0 ? void 0 : opts.axis) === "y" ? "vertical" : "horizontal"),
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext,
        } }, { children: jsx("div", Object.assign({ ref: ref, onKeyDownCapture: handleKeyDown, className: cn("tw:relative", className), role: "region", "aria-roledescription": "carousel" }, props, { children: children })) })));
});
Carousel.displayName = "Carousel";
const CarouselContent = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const { carouselRef, orientation } = useCarousel();
    return (jsx("div", Object.assign({ ref: carouselRef, className: "tw:overflow-hidden" }, { children: jsx("div", Object.assign({ ref: ref, className: cn("tw:flex", orientation === "horizontal" ? "tw:-ml-4" : "tw:-mt-4 tw:flex-col", className) }, props)) })));
});
CarouselContent.displayName = "CarouselContent";
const CarouselItem = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const { orientation } = useCarousel();
    return (jsx("div", Object.assign({ ref: ref, role: "group", "aria-roledescription": "slide", className: cn("tw:min-w-0 tw:shrink-0 tw:grow-0 tw:basis-full", orientation === "horizontal" ? "tw:pl-4" : "tw:pt-4", className) }, props)));
});
CarouselItem.displayName = "CarouselItem";
const CarouselPrevious = React.forwardRef((_a, ref) => {
    var { className, variant = "outline", size = "icon" } = _a, props = __rest(_a, ["className", "variant", "size"]);
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return (jsxs(Button, Object.assign({ ref: ref, variant: variant, size: size, className: cn("tw:absolute tw: tw:h-8 tw:w-8 tw:rounded-full", orientation === "horizontal"
            ? "tw:-left-12 tw:top-1/2 tw:-translate-y-1/2"
            : "tw:-top-12 tw:left-1/2 tw:-translate-x-1/2 tw:rotate-90", className), disabled: !canScrollPrev, onClick: scrollPrev }, props, { children: [jsx(ArrowLeft, { className: "tw:h-4 tw:w-4" }), jsx("span", Object.assign({ className: "tw:sr-only" }, { children: "Previous slide" }))] })));
});
CarouselPrevious.displayName = "CarouselPrevious";
const CarouselNext = React.forwardRef((_a, ref) => {
    var { className, variant = "outline", size = "icon" } = _a, props = __rest(_a, ["className", "variant", "size"]);
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return (jsxs(Button, Object.assign({ ref: ref, variant: variant, size: size, className: cn("tw:absolute tw:h-8 tw:w-8 tw:rounded-full", orientation === "horizontal"
            ? "tw:-right-12 tw:top-1/2 tw:-translate-y-1/2"
            : "tw:-bottom-12 tw:left-1/2 tw:-translate-x-1/2 tw:rotate-90", className), disabled: !canScrollNext, onClick: scrollNext }, props, { children: [jsx(ArrowRight, { className: "tw:h-4 tw:w-4" }), jsx("span", Object.assign({ className: "tw:sr-only" }, { children: "Next slide" }))] })));
});
CarouselNext.displayName = "CarouselNext";

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious };
//# sourceMappingURL=carousel.js.map
